import React, { useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApparelProduct, Family, Student } from "@/types/models";
import ApparelOrderForm from "@/components/apparel/ApparelOrderForm";
import { Button } from "@/components/ui/button";
import { useResponsive } from "@/hooks/useResponsive";
import { RESPONSIVE_SPACING, TOUCH_TARGETS } from "@/design-tokens/responsive";
import { cn } from "@/lib/utils";

interface CartItem {
  product: ApparelProduct;
  quantity: number;
  size?: string;
}

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cartItems: CartItem[];
  families: Family[];
  students: Student[];
  onCheckout: (metadata: any) => void;
  onRemoveProduct: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSize: (index: number, size: string) => void;
}

const CheckoutDialog: React.FC<CheckoutDialogProps> = ({
  open,
  onOpenChange,
  cartItems,
  families,
  students,
  onCheckout,
  onRemoveProduct,
  onUpdateQuantity,
  onUpdateSize,
}) => {
  const formRef = useRef<any>(null);
  const { isMobile } = useResponsive();

  // DEBUG: Log when CheckoutDialog renders
  console.log('CheckoutDialog - Rendering with open:', open);
  console.log('CheckoutDialog - cartItems:', cartItems);

  const handleCheckout = () => {
    const formValues = formRef.current?.getValues?.() || {};
    onCheckout(formValues);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "max-h-[90vh] overflow-y-auto",
        isMobile ? "w-[95vw] max-w-[95vw] h-[90vh]" : "sm:max-w-4xl"
      )}>
        <DialogHeader className={cn(
          isMobile && "sticky top-0 bg-white border-b pb-4 mb-4"
        )}>
          <DialogTitle className={cn(
            isMobile && "text-lg"
          )}>Checkout</DialogTitle>
          <DialogDescription>
            Complete your order details
          </DialogDescription>
        </DialogHeader>
        <div className={cn(
          isMobile && "flex-1 overflow-y-auto"
        )}>
          <ApparelOrderForm
            ref={formRef}
            selectedProducts={cartItems}
            families={families || []}
            students={students || []}
            onRemoveProduct={onRemoveProduct}
            onUpdateQuantity={onUpdateQuantity}
            onUpdateSize={onUpdateSize}
          />
        </div>
        <div className={cn(
          "flex pt-4",
          isMobile ? "sticky bottom-0 bg-white border-t pt-4" : "justify-end"
        )}>
          <Button 
            onClick={handleCheckout}
            className={cn(
              isMobile && "w-full"
            )}
            style={{ minHeight: TOUCH_TARGETS.minSize }}
          >
            Complete Checkout
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
